import React from 'react'
import type { Block, Variable } from '../../types'
import { PropertyLabel } from './shared'
import { Plus, X } from 'lucide-react'
import VariableTextarea from '../VariableTextarea'

interface Message {
  text: string
  sender: string
  name: string
}

interface ChatBubblePropertiesProps {
  block: Block
  variables: Variable[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

export default function ChatBubbleProperties({ block, variables, onUpdateBlock }: ChatBubblePropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const messages = (config.messages as Message[]) || []

  const updateMessages = (newMessages: Message[]) => {
    onUpdateBlock(block.id, { config: { ...config, messages: newMessages } })
  }

  const theme = (config.theme as string) || 'default'

  const updateConfig = (updates: Record<string, unknown>) => {
    onUpdateBlock(block.id, { config: { ...config, ...updates } })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <PropertyLabel>Theme</PropertyLabel>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            { value: 'default', label: 'Default' },
            { value: 'imessage', label: 'iMessage' },
            { value: 'whatsapp', label: 'WhatsApp' },
            { value: 'slack', label: 'Slack' },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateConfig({ theme: opt.value })}
              className={`py-2 px-2 rounded-lg border text-xs font-medium transition-all ${
                theme === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <PropertyLabel>Messages</PropertyLabel>
      {messages.map((msg, i) => (
        <div key={i} className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">Message {i + 1}</span>
            <button
              onClick={() => {
                const newMessages = [...messages]
                newMessages.splice(i, 1)
                updateMessages(newMessages)
              }}
              className="p-0.5 hover:bg-slate-200 rounded"
            >
              <X className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </div>
          <input
            type="text"
            value={msg.name}
            onChange={(e) => {
              const newMessages = [...messages]
              newMessages[i] = { ...newMessages[i], name: e.target.value }
              updateMessages(newMessages)
            }}
            placeholder="Name"
            className="w-full bg-white border border-slate-200 rounded p-1.5 text-xs focus:ring-1 focus:ring-[#5048e5] outline-none"
          />
          <VariableTextarea
            value={msg.text}
            onChange={(text) => {
              const newMessages = [...messages]
              newMessages[i] = { ...newMessages[i], text }
              updateMessages(newMessages)
            }}
            variables={variables}
            placeholder="Message text (type @ for variables)"
          />
          <select
            value={msg.sender}
            onChange={(e) => {
              const newMessages = [...messages]
              newMessages[i] = { ...newMessages[i], sender: e.target.value }
              updateMessages(newMessages)
            }}
            className="w-full bg-white border border-slate-200 rounded p-1.5 text-xs focus:ring-1 focus:ring-[#5048e5] outline-none"
          >
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </div>
      ))}
      <button
        onClick={() => updateMessages([...messages, { text: 'New message', sender: 'left', name: 'User' }])}
        className="flex items-center gap-1 text-xs text-[#5048e5] hover:text-[#5048e5]/80 font-medium"
      >
        <Plus className="h-3.5 w-3.5" /> Add Message
      </button>
    </div>
  )
}
